import React, {createRef, ReactNode, RefObject} from "react";
import LengthUnit from "../../../shared/units/LengthUnit";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import {PortalGlobals} from "../../Portal";
import UnitHelper from "../../../shared/helper/UnitHelper";
import {pdfjs} from "react-pdf";
import DomHelper from "../../../shared/helper/DomHelper";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import Immutable from "../../../shared/immutable/Immutable";
import UserSettings from "../../session/UserSettings";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

const STEP_WIDTH: number = 100;
export const RULER_PADDING: number = 30;

export enum RulerDirection {
	HORIZONTAL = "horizontal",
	VERTICAL = "vertical"
}

interface RulerProperties extends PortalGlobals, DebugGlobals {
	direction: RulerDirection;
	scrollTop: number;
	scrollLeft: number;
	scrollWidth: number;
	scrollHeight: number;
	currentPage: number;
	zoomValue: number;
	scrollOffset: number;
	portalDocument: Immutable<PortalDocumentInterface>;
}

interface RulerStatus {
	originalWidth: number;
	originalHeight: number;
	pageWidth: number;
	pageHeight: number;
	offsetTop: number;
	offsetLeft: number;
	clientX: number;
	clientY: number;
}

export class RulerDefinition extends PortalPureComponent<RulerProperties, RulerStatus> {
	private rulerElement: RefObject<HTMLDivElement>;

	constructor(props: Readonly<RulerProperties>) {
		super(props);

		this.rulerElement = createRef<HTMLDivElement>();

		this.state = {
			originalWidth: 0,
			originalHeight: 0,
			pageWidth: 0,
			pageHeight: 0,
			offsetTop: 0,
			offsetLeft: 0,
			clientX: 0,
			clientY: 0
		};

		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.deferredUpdatePage = FunctionHelper.defer(this.deferredUpdatePage.bind(this));
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener("pointermove", this.handleMouseMove);

		await this.deferredUpdatePage();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		window.removeEventListener("pointermove", this.handleMouseMove);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<RulerProperties>, prevState: Readonly<RulerStatus>
	): Promise<void> {
		await this.deferredUpdatePage();
	}

	private async deferredUpdatePage(): Promise<void> {
		if (!this.mounted) {
			return;
		}

		let pageElement: HTMLElement | null = DomHelper.find(
			document.body, ".viewer-screen .viewer-container .pdf-viewer .page.current"
		);

		if (!FunctionHelper.exists(pageElement)) {
			await this.deferredUpdatePage();
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		try {
			let viewerData: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let pdfPage: pdfjs.PDFPageProxy = await viewerData.getPage(this.props.currentPage);
			let originalViewport: pdfjs.PageViewport = pdfPage.getViewport({scale: 1});
			let scaledViewport: pdfjs.PageViewport = pdfPage.getViewport({scale: this.props.zoomValue / 100});
			let offsetTop: number = pageElement!.offsetTop;
			let offsetLeft: number = pageElement!.offsetLeft;

			this.setState({
				originalHeight: originalViewport.height,
				originalWidth: originalViewport.width,
				pageHeight: scaledViewport.height,
				pageWidth: scaledViewport.width,
				offsetTop: offsetTop,
				offsetLeft: offsetLeft
			});
		} catch (e: any) {
			// can be ignored here
		}
	}

	private handleMouseMove(event: { clientX: number, clientY: number }): void {
		if (this.rulerElement.current === null) {
			return;
		}

		let rulerRect: DOMRect = this.rulerElement.current.getBoundingClientRect();

		this.setState({
			clientX: event.clientX - rulerRect.x,
			clientY: event.clientY - rulerRect.y
		});
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let userSettings: UserSettings = UserSettings.fromJson(this.props.userSettings.get());
		let steps: Array<ReactNode> = [];
		let size: number =
			this.props.direction === RulerDirection.VERTICAL ? this.state.pageHeight : this.state.pageWidth;
		let originalSize: number =
			this.props.direction === RulerDirection.VERTICAL ? this.state.originalHeight : this.state.originalWidth;
		let stepCount: number = Math.ceil(size / STEP_WIDTH);
		let stepScale: number = UnitHelper.pxToUnit(
			originalSize, userSettings.unit, this.props.portalResolution
		) / stepCount;
		let stepWidth: number = Math.round(size / stepCount);

		if (this.props.direction === RulerDirection.HORIZONTAL && this.props.scrollWidth > 0) {
			stepCount = Math.ceil(this.props.scrollWidth / stepWidth);
		} else if (this.props.direction === RulerDirection.VERTICAL && this.props.scrollHeight > 0) {
			stepCount = Math.ceil(this.props.scrollHeight / stepWidth);
		}
		stepCount += 10; // add extra steps for overflow

		let margin: number = 0;
		for (let i: number = 0; i <= stepCount; i++) {
			// add positive step
			steps.push(
				<p style={
					this.props.direction === RulerDirection.HORIZONTAL ?
						{width: stepWidth + LengthUnit.PIXEL, left: margin + LengthUnit.PIXEL} :
						{height: stepWidth + LengthUnit.PIXEL, top: margin + LengthUnit.PIXEL}
				} key={i + "+"}>
					<span className="label">{Math.round(i * stepScale).toString()}</span>
					<span className="full-line"/>
					<span className="half-line"/>
				</p>
			);

			if (i === 0) {
				margin += stepWidth;
				continue;
			}

			// add negative step
			steps.push(
				<p style={
					this.props.direction === RulerDirection.HORIZONTAL ?
						{width: stepWidth + LengthUnit.PIXEL, left: -margin + LengthUnit.PIXEL} :
						{height: stepWidth + LengthUnit.PIXEL, top: -margin + LengthUnit.PIXEL}
				} key={i + "-"}>
					<span className="label">{Math.round(i * stepScale).toString()}</span>
					<span className="full-line"/>
					<span className="half-line"/>
				</p>
			);

			margin += stepWidth;
		}

		return (
			<div className={"ruler " + this.props.direction} ref={this.rulerElement}>
				<div className="body" style={
					this.props.direction === RulerDirection.VERTICAL ?
						{
							top: -this.props.scrollTop + this.props.scrollOffset + LengthUnit.PIXEL,
							marginTop: this.state.offsetTop + LengthUnit.PIXEL
						} :
						{
							left: -this.props.scrollLeft + LengthUnit.PIXEL,
							marginLeft: this.state.offsetLeft + LengthUnit.PIXEL
						}
				}>
					<div className="steps">{steps}</div>
				</div>
				<div className="indent" style={
					this.props.direction === RulerDirection.VERTICAL ?
						{top: this.state.clientY + LengthUnit.PIXEL} :
						{left: this.state.clientX + LengthUnit.PIXEL}
				}/>
			</div>
		);
	}
}

const Ruler = React.memo(RulerDefinition,
	(prevProps: Readonly<RulerProperties>, nextProps: Readonly<RulerProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument) &&
			(
				nextProps.portalDocument.get().id !== prevProps.portalDocument?.get().id ||
				nextProps.portalDocument.get().updated !== prevProps.portalDocument?.get().updated
			)
		) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.userSettings) && !nextProps.userSettings.equals(prevProps.userSettings)) {
			return false;
		}

		if (nextProps.direction !== prevProps.direction) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.currentPage !== prevProps.currentPage) {
			return false;
		}

		if (nextProps.scrollOffset !== prevProps.scrollOffset) {
			return false;
		}

		if (nextProps.scrollTop !== prevProps.scrollTop ||
			nextProps.scrollLeft !== prevProps.scrollLeft ||
			nextProps.scrollHeight !== prevProps.scrollHeight ||
			nextProps.scrollWidth !== prevProps.scrollWidth) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		return true;
	}
);

export default Ruler;